import { rankItem } from '@tanstack/match-sorter-utils';
import { createColumnHelper } from '@tanstack/react-table';
import { StatusPill } from 'component-library';
import { BoatModel, Oem, PropulsionSystem } from '../models/ApiModels';
import { GetStatusColor } from './Custom';
import { ListFilterOption } from './UseListViewFilters';

export const BOAT_MODEL_LENGTH_RANGE_LABELS = [
  '<6m',
  '6-8m',
  '8-10m',
  '10-12m',
  '12-14m',
  '14-16m',
  '>16m',
  'Unknown',
] as const;

const BOAT_MODEL_LENGTH_RANGE_IMPERIAL_LABELS = [
  '<20ft',
  '20-26ft',
  '26-33ft',
  '33-39ft',
  '39-46ft',
  '46-52ft',
  '>52ft',
  'Unknown',
] as const;

export type BoatModelLengthRangeLabel = typeof BOAT_MODEL_LENGTH_RANGE_LABELS[number];

export function getBoatLengthRangeDisplayLabel(label: string, useImperial = false): string {
  if (!useImperial) return label;

  const rangeIndex = BOAT_MODEL_LENGTH_RANGE_LABELS.indexOf(label as BoatModelLengthRangeLabel);
  if (rangeIndex === -1) return label;

  return BOAT_MODEL_LENGTH_RANGE_IMPERIAL_LABELS[rangeIndex] ?? label;
}

export function getBoatLengthRangeLabel(length?: number): BoatModelLengthRangeLabel {
  if (typeof length !== 'number' || Number.isNaN(length)) return 'Unknown';
  if (length < 6) return '<6m';
  if (length < 8) return '6-8m';
  if (length < 10) return '8-10m';
  if (length < 12) return '10-12m';
  if (length < 14) return '12-14m';
  if (length < 16) return '14-16m';
  return '>16m';
}

export type BoatModelsList = {
  oemName?: string;
  boatMake?: string;
  boatModel: string;
  lengthRange: string;
  series?: string;
  modelYear?: string;
  propulsion?: string[];
  status: string;
  boatModelId?: string;
};

export type BoatModelColumnFilters = {
  oemName: string[];
  boatMake: string[];
  lengthRange: string[];
  status: string[];
};

export type BoatModelColumnFilterKey = keyof BoatModelColumnFilters;

type BoatModelFilterColumnDefinition = {
  key: BoatModelColumnFilterKey;
  label: string;
  searchable?: boolean;
};

export type BoatModelFilterOptionsByColumn = Record<BoatModelColumnFilterKey, ListFilterOption[]>;

export const boatModelsDefaultColumnFilters: BoatModelColumnFilters = {
  oemName: [],
  boatMake: [],
  lengthRange: [],
  status: [],
};

export const boatModelsDefaultFilterSearch: Partial<Record<BoatModelColumnFilterKey, string>> = {
  oemName: '',
  boatMake: '',
};

const boatModelsFilterColumns: BoatModelFilterColumnDefinition[] = [
  { key: 'oemName', label: 'Owner', searchable: true },
  { key: 'boatMake', label: 'Make', searchable: true },
  { key: 'lengthRange', label: 'Length' },
  { key: 'status', label: 'Status' },
];

function normalizeSearchValue(value: string) {
  return value.trim().toLowerCase();
}

function addSearchableValue(values: string[], value: unknown, visited: Set<unknown>) {
  if (value === null || value === undefined) return;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    values.push(String(value));
    return;
  }

  if (value instanceof Date) {
    values.push(value.toISOString());
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((arrayValue) => addSearchableValue(values, arrayValue, visited));
    return;
  }

  if (typeof value === 'object') {
    if (visited.has(value)) return;
    visited.add(value);
    Object.values(value).forEach((objectValue) => addSearchableValue(values, objectValue, visited));
  }
}

export function FilterBoatModelsBySearch(boatModels: BoatModelsList[], searchValue: string) {
  const query = normalizeSearchValue(searchValue);
  if (!query) return boatModels;

  return boatModels.filter((boatModel) => {
    const values: string[] = [];
    addSearchableValue(values, boatModel, new Set<unknown>());
    return values.some((value) => rankItem(value, query).passed);
  });
}

export function GetBoatModelsListData(
  boatModels: BoatModel[],
  propulsionSystems: PropulsionSystem[],
  oem: Oem,
  oems: Oem[]
) {
  const modelList: BoatModelsList[] = boatModels.map((boatModel: BoatModel) => {
    const findOem = oems.find((oem: Oem) => oem.id === boatModel.oemTenantId)?.name;
    return {
      oemName: oem?.name ? oem.name : findOem ? findOem : '',
      boatMake: boatModel.make,
      boatModel: boatModel.name,
      lengthRange: getBoatLengthRangeLabel(boatModel.length),
      propulsion: propulsionSystems
        .filter((system: PropulsionSystem) => {
          return boatModel.propulsionSystemIds.includes(system.id);
        })
        .map((system: PropulsionSystem) => {
          return system.name;
        }),
      status: boatModel.status,
      boatModelId: boatModel.id,
    };
  });
  return modelList;
}

export function getBoatModelsFilterColumnScrollHeightClass(key: BoatModelColumnFilterKey) {
  if (key === 'lengthRange') return 'max-h-72';
  if (key === 'status') return 'max-h-64';
  return 'max-h-52';
}

export function getBoatModelsFilterValueForColumn(boatModel: BoatModelsList, key: BoatModelColumnFilterKey): string {
  if (key === 'lengthRange') return boatModel.lengthRange?.trim() || 'Unknown';
  return `${boatModel[key] ?? ''}`.trim();
}

export function buildBoatModelsFilterOptionsByColumn(
  boatModels: BoatModelsList[],
  buildFilterOptions: (values: (string | number | undefined)[]) => ListFilterOption[]
): BoatModelFilterOptionsByColumn {
  return {
    oemName: buildFilterOptions(boatModels.map((boatModel) => boatModel.oemName)),
    boatMake: buildFilterOptions(boatModels.map((boatModel) => boatModel.boatMake)),
    lengthRange: BOAT_MODEL_LENGTH_RANGE_LABELS.map((rangeLabel) => ({ label: rangeLabel, value: rangeLabel })),
    status: buildFilterOptions(boatModels.map((boatModel) => boatModel.status)),
  };
}

export function buildBoatModelsFilterPanelColumns(filterOptionsByColumn: BoatModelFilterOptionsByColumn) {
  return boatModelsFilterColumns.map((column) => ({
    key: column.key,
    label: column.label,
    options: filterOptionsByColumn[column.key],
    searchable: column.searchable,
    scrollClassName: getBoatModelsFilterColumnScrollHeightClass(column.key),
  }));
}

const columnHelper = createColumnHelper<BoatModelsList>();

export const boatModelsListColumns = [
  columnHelper.accessor('oemName', {
    header: 'Customer',
    cell: (info) => {
      return <div className='truncate'>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('boatMake', {
    header: 'Make',
    cell: (info) => {
      return <div className='truncate'>{info.getValue()}</div>;
    },
    enableHiding: true,
  }),
  columnHelper.accessor('boatModel', {
    header: 'Model',
    cell: (info) => {
      return <div className='truncate'>{info.getValue()}</div>;
    },
    enableHiding: true,
  }),
  columnHelper.accessor('propulsion', {
    header: 'Propulsion',
    cell: (info) => {
      const propulsionSystems = info.getValue();
      return (
        <div className='flex flex-wrap sm:flex-nowrap gap-2'>
          {propulsionSystems && propulsionSystems.length > 0 ? (
            propulsionSystems?.map((i: string, index) => {
              return (
                <div key={index}>
                  <StatusPill color='Dark Gray' text={i} />
                </div>
              );
            })
          ) : (
            <StatusPill color='Light Gray' text='Unknown' />
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    enableGlobalFilter: false,
    cell: (info) => <StatusPill color={GetStatusColor(info.getValue()) as any} text={info.getValue()} fixedSize />,
    size: 200,
  }),
];
